import React, { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import useAsync from "../../hooks/useAsync";
import { datesAPI, money, dateRange, daysNights, monthLabel } from "../../services/api";
import Footer from "../home/footer";
import "./UpcomingRetreats.css";
import Picture from "../utils/Picture";

/* Lengths the filter offers; each date falls in exactly one. */
const LENGTHS = [
  { id: "short", label: "Up to 4 nights", test: (n) => n <= 4 },
  { id: "week", label: "5 to 8 nights", test: (n) => n >= 5 && n <= 8 },
  { id: "long", label: "9 nights or more", test: (n) => n >= 9 },
];

/** Months shown open before the rest fold into one line each. */
const OPEN_MONTHS = 3;

const monthKey = (d) => String(d).slice(0, 7);

/**
 * Upcoming retreats: every published date, chronological, grouped by
 * month. Filters live in the address (?month=2026-11&type=ayahuasca-retreat&length=week)
 * so a filtered list can be shared. Built to stay readable with dozens of dates.
 */
const UpcomingRetreats = () => {
  const [params, setParams] = useSearchParams();
  const { data, loading, error, reload } = useAsync(() => datesAPI.list(), []);
  const [opened, setOpened] = useState({});

  const month = params.get("month") || "";
  const type = params.get("type") || "";
  const length = params.get("length") || "";
  const filtered = !!(month || type || length);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const dates = useMemo(() => data?.dates || [], [data]);
  const months = useMemo(() => [...new Set(dates.map((d) => monthKey(d.startDate)))], [dates]);
  const shown = useMemo(() => dates.filter((d) =>
    (!month || monthKey(d.startDate) === month) &&
    (!type || d.typeSlug === type) &&
    (!length || LENGTHS.find((l) => l.id === length)?.test(d.nights))), [dates, month, type, length]);
  const groups = useMemo(() => {
    const m = new Map();
    shown.forEach((d) => m.set(monthKey(d.startDate), [...(m.get(monthKey(d.startDate)) || []), d]));
    return [...m.entries()];
  }, [shown]);

  return (
    <>
      <header className="ur-head grain-bg">
        <div className="ur-head-inner">
          <h1 className="ur-title hFont">Upcoming retreats</h1>
          <p className="ur-intro">Every date we have open, in order. Choose one to see what it includes and reserve your place.</p>
        </div>
      </header>

      <main className="ur-page grain-bg-light">
        <div className="ur-wrap">
          <div className="ur-filters" role="search" aria-label="Filter retreats">
            <label className="ur-filter">
              <span>Month</span>
              <select value={month} onChange={(e) => setFilter("month", e.target.value)}>
                <option value="">Any month</option>
                {months.map((m) => <option key={m} value={m}>{monthLabel(`${m}-01`)}</option>)}
              </select>
            </label>
            <label className="ur-filter">
              <span>Retreat</span>
              <select value={type} onChange={(e) => setFilter("type", e.target.value)}>
                <option value="">Any retreat</option>
                {(data?.types || []).map((t) => <option key={t.id} value={t.slug}>{t.name}</option>)}
              </select>
            </label>
            <label className="ur-filter">
              <span>Length</span>
              <select value={length} onChange={(e) => setFilter("length", e.target.value)}>
                <option value="">Any length</option>
                {LENGTHS.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </label>
            {filtered && (
              <button type="button" className="ur-clear" onClick={() => setParams({}, { replace: true })}>Clear filters</button>
            )}
          </div>

          {loading && <p className="mk-muted ur-state">Loading dates…</p>}
          {error && (
            <div className="ur-state">
              <p className="mk-muted">We couldn't load the dates just now.</p>
              <button className="mk-btn mk-btn--ghost" onClick={reload}>Try again</button>
            </div>
          )}
          {!loading && !error && groups.length === 0 && (
            <div className="ur-state">
              <p className="mk-muted">
                {filtered ? "No dates match those filters." : "There are no dates open right now. New ones are added through the year."}
              </p>
              {filtered
                ? <button className="mk-btn mk-btn--ghost" onClick={() => setParams({}, { replace: true })}>Clear filters</button>
                : <Link className="mk-btn mk-btn--primary" to="/contact">Ask us about dates</Link>}
            </div>
          )}

          {groups.map(([key, list], i) => {
            const open = filtered || i < OPEN_MONTHS || opened[key];
            const label = monthLabel(`${key}-01`);
            return (
              <section key={key} className="ur-month" aria-labelledby={`m-${key}`}>
                {open ? (
                  <>
                    <h2 id={`m-${key}`} className="ur-month-name hFont">
                      {label.split(" ")[0]}<span>{label.split(" ")[1]}</span>
                    </h2>
                    <ul className="ur-list">
                      {list.map((d) => <DateRow key={d.id} d={d} />)}
                    </ul>
                  </>
                ) : (
                  <button type="button" id={`m-${key}`} className="ur-month-closed" aria-expanded="false" onClick={() => setOpened((o) => ({ ...o, [key]: true }))}>
                    <span className="hFont">{label}</span>
                    <span>{list.length} {list.length === 1 ? "retreat" : "retreats"}</span>
                  </button>
                )}
              </section>
            );
          })}
        </div>
      </main>
      <Footer />
    </>
  );
};

const DateRow = ({ d }) => {
  const to = d.typeSlug ? `/retreats/${d.typeSlug}?date=${d.id}` : `/retreats/${d.slug}`;
  return (
    <li className="ur-row">
      <Picture className="ur-img" src={d.image || "/events/events1.png"} sizes="132px" width={132} height={92} />
      <div className="ur-what">
        <h3 className="ur-name"><Link to={to}>{d.title}</Link></h3>
        {d.tagline && <p className="ur-tagline">{d.tagline}</p>}
        {d.tags?.length > 0 && <p className="ur-tags">{d.tags.map((t) => <span key={t}>{t}</span>)}</p>}
      </div>
      <p className="ur-when">
        <b>{dateRange(d.startDate, d.endDate)}</b>
        <span>{daysNights(d.nights)}</span>
      </p>
      <p className={`ur-left ${d.soldOut ? "is-full" : d.placesLeft <= 3 ? "is-low" : ""}`}>
        {d.soldOut ? (d.waitlist ? "Full, waiting list open" : "Full") : `${d.placesLeft} ${d.placesLeft === 1 ? "space" : "spaces"} left`}
        <span>from {money(d.priceFrom)}</span>
      </p>
      <Link className="ur-go" to={to} aria-label={`View ${d.title}, ${dateRange(d.startDate, d.endDate)}`}>View retreat</Link>
    </li>
  );
};

export default UpcomingRetreats;
